/**
 * ORION — Governance Domain
 *
 * File: VotingPanel.tsx
 *
 * Responsibility:
 * - UI for agents/humans to cast votes on a context.
 * - Displays current voting tally and status.
 *
 * What this file does NOT do:
 * - Does not calculate the winner (Backend logic).
 * - Does not enforce permissions (Backend/Service logic).
 *
 * Depends on:
 * - governanceService
 */
import React, { useState, useEffect, useRef } from 'react';
import { governanceService } from '../governanceService';
import type { VoteContext, VoteDistribution, VoteRecommendation, Vote } from '../governanceService';
import { VoteContextHeader } from './VoteContextHeader';
import { CreateContextForm } from './CreateContextForm';
import { VoteForm } from './VoteForm';
import { VoteDistribution as VoteDistributionComponent } from './VoteDistribution';
import { VoteRecommendation as VoteRecommendationComponent } from './VoteRecommendation';
import { getToken } from '../../../utils/auth';

interface VotingPanelProps {
    objectId: string;
    objectState: string;
    onUpdate: () => void;
    isActiveTab: boolean; // CRITICAL: Only poll when this tab is active
}

export const VotingPanel: React.FC<VotingPanelProps> = ({
    objectId,
    objectState,
    onUpdate,
    isActiveTab
}) => {
    const [contexts, setContexts] = useState<VoteContext[]>([]);
    const [activeContext, setActiveContext] = useState<VoteContext | null>(null);
    const [distribution, setDistribution] = useState<VoteDistribution | null>(null);
    const [recommendation, setRecommendation] = useState<VoteRecommendation | null>(null);
    const [userVote, setUserVote] = useState<Vote | null>(null);

    const [error, setError] = useState<string>('');
    const [showCreateForm, setShowCreateForm] = useState(false);

    // CRITICAL: Use ref to prevent interval duplication
    const pollingIntervalRef = useRef<number | null>(null);
    const isSubmittingRef = useRef(false); // Track submit state to pause polling

    // Get current user ID from token
    const getUserId = () => {
        const token = getToken();
        if (!token) return null;
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.sub || payload.user_id;
        } catch {
            return null;
        }
    };

    // Determine active context (prefer OPEN, then most recent)
    const determineActiveContext = (ctxs: VoteContext[]): VoteContext | null => {
        if (ctxs.length === 0) return null;

        // Check for multiple OPEN contexts (warning case)
        const openContexts = ctxs.filter(c => c.status === 'OPEN');
        if (openContexts.length > 1) {
            console.warn(`Multiple OPEN contexts found for object ${objectId}. This should not happen.`);
            setError('Warning: Multiple open voting contexts detected. Please close one.');
        }

        // Prefer OPEN
        if (openContexts.length > 0) {
            return openContexts[0];
        }

        // Otherwise, most recent by created_at
        const sorted = [...ctxs].sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        return sorted[0];
    };

    // Determine polling interval based on context status
    const getPollingInterval = (context: VoteContext | null): number => {
        if (!context) return 30000; // 30s - no context, only check for new contexts

        switch (context.status) {
            case 'OPEN':
                return 5000; // 5s - active voting
            case 'CLOSED':
            case 'TIED':
            case 'EXPIRED':
                return 15000; // 15s - read-only
            default:
                return 15000;
        }
    };

    // Single poll function (called by interval and manual refresh)
    const pollOnce = async () => {
        // Don't poll during submit (race control)
        if (isSubmittingRef.current) return;

        try {
            // Always fetch contexts
            const ctxs = await governanceService.listVoteContexts(objectId);
            setContexts(ctxs);

            const active = determineActiveContext(ctxs);
            setActiveContext(active);

            if (active) {
                // Fetch distribution if context exists
                const dist = await governanceService.getDistribution(active.id);
                setDistribution(dist);

                // Check if user has voted
                const userId = getUserId();
                if (userId && dist) {
                    let foundVote: Vote | null = null;
                    for (const [choice, data] of Object.entries(dist.by_choice)) {
                        const voter = data.voters.find(v => v.voter_id === userId);
                        if (voter) {
                            foundVote = {
                                id: '', // Not needed for display
                                context_id: active.id,
                                voter_id: userId,
                                voter_type: voter.voter_type as 'HUMAN' | 'AGENT',
                                choice,
                                justification_preview: voter.justification_preview,
                                has_full_justification: voter.has_full_justification,
                                weight_applied: 0, // Not in distribution response
                                created_at: ''
                            };
                            break;
                        }
                    }
                    setUserVote(foundVote);
                }

                // Fetch recommendation
                const rec = await governanceService.getRecommendation(objectId);
                setRecommendation(rec);
            } else {
                setDistribution(null);
                setUserVote(null);
                setRecommendation(null);
            }
        } catch (err: any) {
            console.error('Polling error:', err);
            // Don't show error for polling failures (silent fail)
        }
    };

    // CRITICAL: Polling setup with cleanup
    useEffect(() => {
        // Don't poll if tab is not active
        if (!isActiveTab) {
            // Clear any existing interval
            if (pollingIntervalRef.current !== null) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            return;
        }

        // Initial poll
        pollOnce();

        // Determine interval
        const interval = getPollingInterval(activeContext);

        // Clear any existing interval before creating new one
        if (pollingIntervalRef.current !== null) {
            clearInterval(pollingIntervalRef.current);
        }

        // Create new interval
        pollingIntervalRef.current = window.setInterval(pollOnce, interval);

        // CRITICAL: Cleanup on unmount or when dependencies change
        return () => {
            if (pollingIntervalRef.current !== null) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [isActiveTab, objectId, activeContext?.status]); // Re-setup when tab active state or interval changes

    // Manual refresh (does NOT create new interval)
    const handleRefresh = () => {
        pollOnce(); // Just poll once, interval continues as-is
    };

    // CRITICAL: Race control - pause polling during submit
    const withSubmitControl = async (fn: () => Promise<void>) => {
        isSubmittingRef.current = true;

        // Pause polling
        if (pollingIntervalRef.current !== null) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }

        try {
            await fn();
            // Immediate refresh after submit
            await pollOnce();
        } finally {
            isSubmittingRef.current = false;

            // Resume polling
            const interval = getPollingInterval(activeContext);
            pollingIntervalRef.current = window.setInterval(pollOnce, interval);
        }
    };

    const handleCreateContext = async (type: 'CONTENT' | 'PROCESS', options: string[], hours: number) => {
        await withSubmitControl(async () => {
            await governanceService.createVoteContext(objectId, type, options, hours);
            setShowCreateForm(false);
            setError('');
            onUpdate(); // Notify parent
        });
    };

    const handleVote = async (choice: string, justification: string) => {
        if (!activeContext) return;

        await withSubmitControl(async () => {
            await governanceService.castVote(activeContext.id, choice, justification);
            setError('');
            onUpdate(); // Notify parent
        });
    };

    const handleCloseContext = async () => {
        if (!activeContext) return;

        const confirmed = window.confirm('Are you sure you want to close this voting context? This cannot be undone.');
        if (!confirmed) return;

        await withSubmitControl(async () => {
            await governanceService.closeContext(activeContext.id);
            setError('');
            onUpdate(); // Notify parent
        });
    };

    // Determine if current user is creator
    const isCreator = activeContext && getUserId() === activeContext.created_by;

    // Render logic
    const renderContent = () => {
        // No contexts and not eligible to create
        if (contexts.length === 0 && objectState !== 'FINALIST') {
            return (
                <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                    <div className="text-4xl mb-3">🗳️</div>
                    <p className="text-sm">No voting contexts</p>
                    <p className="text-xs mt-1">This object doesn't have any voting contexts yet.</p>
                </div>
            );
        }

        // Show create form
        if (showCreateForm) {
            return (
                <CreateContextForm
                    onSubmit={handleCreateContext}
                    onCancel={() => setShowCreateForm(false)}
                />
            );
        }

        // Active context exists
        if (activeContext) {
            const hasOpenContext = contexts.some(c => c.status === 'OPEN');

            return (
                <>
                    <VoteContextHeader
                        context={activeContext}
                        isCreator={!!isCreator}
                        onClose={handleCloseContext}
                        onRefresh={handleRefresh}
                    />

                    {activeContext.status === 'OPEN' && !userVote && (
                        <VoteForm
                            context={activeContext}
                            onVote={handleVote}
                        />
                    )}

                    {userVote && (
                        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                            <div className="flex items-center gap-2 text-sm">
                                <span className="text-green-600 dark:text-green-400">✓</span>
                                <span className="text-green-700 dark:text-green-300 font-medium">
                                    You voted: <span className="font-mono font-bold">{userVote.choice}</span>
                                </span>
                            </div>
                        </div>
                    )}

                    {distribution && distribution.total_votes > 0 && (
                        <VoteDistributionComponent
                            distribution={distribution}
                            userChoice={userVote?.choice}
                        />
                    )}

                    {recommendation && (
                        <VoteRecommendationComponent recommendation={recommendation} />
                    )}

                    {!hasOpenContext && (
                        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 text-center">
                            <p className="text-sm text-slate-500 mb-3">Voting context is {activeContext.status.toLowerCase()}.</p>
                            <button
                                onClick={() => setShowCreateForm(true)}
                                className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                            >
                                Open New Vote Context
                            </button>
                        </div>
                    )}
                </>
            );
        }

        // Can create new context
        const hasOpenContext = contexts.some(c => c.status === 'OPEN');
        if (!hasOpenContext && (objectState === 'FINALIST' || contexts.length > 0)) {
            return (
                <div className="text-center py-8">
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Open Vote Context
                    </button>
                </div>
            );
        }

        return null;
    };

    return (
        <div className="space-y-4">
            {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
                    ❌ {error}
                </div>
            )}

            {renderContent()}
        </div>
    );
};
